import { useTranslation } from "react-i18next"

import { useNotification } from "@/contexts/notification-context"
import type { Organization } from "@/models/organization"
import { getAdminConfirmDeleteDialogDefaults } from "@/modules/admin/utils/get-admin-confirm-delete-dialog-defaults"
import { useDeleteCredential } from "@/services/credential"
import { getErrorNotificationMessage } from "@/utils/notification-bridge"
import { useConfirm } from "material-ui-confirm"

interface CredentialDeleteTarget {
  id: string
  providerName: string
}

interface UseConfirmDeleteCredentialOptions {
  onSuccess?: (credentialId: string) => void
  organizationId: Organization["id"]
}

export const useConfirmDeleteCredential = ({
  onSuccess,
  organizationId,
}: UseConfirmDeleteCredentialOptions) => {
  const { t } = useTranslation("common")
  const confirm = useConfirm()
  const { setNotification } = useNotification()
  const { deleteCredentialIsPending, deleteCredentialMutateAsync } =
    useDeleteCredential()

  const confirmAndDeleteCredential = async ({
    id,
    providerName,
  }: CredentialDeleteTarget): Promise<void> => {
    const { confirmed } = await confirm({
      title: t("admin.credentialsUi.deleteConfirmTitle"),
      description: t("admin.credentialsUi.deleteConfirmMessage", {
        providerName,
      }),
      ...getAdminConfirmDeleteDialogDefaults(t),
    })

    if (!confirmed) {
      return
    }

    try {
      await deleteCredentialMutateAsync({
        credentialId: id,
        organizationId,
      })
      onSuccess?.(id)
    } catch (error: unknown) {
      setNotification({
        message: getErrorNotificationMessage(
          error,
          t("admin.credentialsUi.deleteError")
        ),
        type: "error",
      })
    }
  }

  return {
    confirmAndDeleteCredential,
    deleteCredentialIsPending,
  }
}
